"use client"

import React, { useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import * as XLSX from "xlsx"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ArrowLeft, ArrowRight, CheckCircle2, Download, FileSpreadsheet, Loader2, Plus, Trash2, XCircle } from "lucide-react"
import { Blade } from "@/components/blades/blade"
import { toast } from "sonner"
import { downloadBlob, useExplorer, useTenantAtsSystem } from "@/hooks/use-explorer"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { useEntityTypeDetail } from "@/hooks/use-entity-type-detail"
import { GroupedEntitySelectItems } from "@/components/shared/entity-browser"
import { useOnboardingExport } from "@/hooks/use-onboarding-export"
import { FilterBar, getMissingRequiredFilters, toExplorerFilters, type FilterValues } from "@/components/explorer/filter-bar"
import { AtsFieldPathPicker } from "@/components/shared/ats-field-path-picker"
import { ArrayPathRefiner } from "@/components/shared/array-path-refiner"
import { FieldSelectionSection } from "@/components/data-pull/field-selection-section"
import { SavedSelectionsMenu } from "@/components/data-pull/saved-selections-menu"
import { isColumnField } from "@/lib/ats-schema-expansion"
import { pullErrorMessage } from "@/lib/pull-errors"
import { csvToWorkbook, recordsToWorkbook, type SheetField } from "@/lib/pull-workbook"
import { buildPreviewFields, buildPullFields, defaultValueFormat, fieldKey, renameSectionKeys, withoutSectionKeys } from "@/lib/pull-request-fields"
import {
  localSelectionStorage,
  pullPresetsStorageKey,
  pullSelectionStorageKey,
  readPullPresets,
  readStoredPullSelection,
  withPreset,
  withoutPreset,
  writePullPresets,
  writeStoredPullSelection,
  type PullSelection,
  type PullSelectionPreset,
} from "@/lib/pull-selection-storage"
import { activeToManyModes, itemPathsUnder, parentIdPathOf, planToMany, renameSectionModes, rowsRootKey, sheetRootKeys, toManyRootKeysOf, withoutSectionModes, type PullToManyModes } from "@/lib/pull-to-many"
import { isRecord } from "@/lib/utils"
import {
  PULL_MAX_RECORDS,
  PULL_MAX_RECORDS_COMPOSITE,
  type AtsEntityTypeInfo,
  type AtsFieldDescriptor,
  type AtsPullFormat,
  type AtsPullRelatedEntity,
  type AtsPullValueFormat,
  type AtsRecordPage,
} from "@/types/ats-explorer-api"

const STEP_KEYS: readonly TranslationKey[] = ["pull.steps.entitySearch", "pull.steps.fields", "pull.steps.preview", "pull.steps.export"]

/** One entity whose fields the wizard offers: the primary entity or a related entity */
interface FieldSection {
  /** Output key — the primary entity type, or the related entity's Name */
  key: string
  title: string
  entityInfo: AtsEntityTypeInfo | null
}

export interface PullBladeInitial {
  entityName?: string
  searchText?: string
  filterValues?: FilterValues
  /** Column order of the explorer grid, kept as the file's column order */
  selectedFields?: string[]
  /** True when the scope comes from the explorer, so the wizard can say why it is prefilled */
  carriedFromExplorer?: boolean
}

export interface PullBladeProps {
  open: boolean
  onClose: () => void
  depth?: number
  trail?: string[]
  onTrailClick?: (index: number) => void
  /** Scope carried in from the explorer: entity, keyword, filters and selected columns */
  initial?: PullBladeInitial
}

/**
 * The Data Pull as a wide blade with a step rail (Scope · Fields · Preview · Export),
 * so a pull can be configured over whatever the user was looking at. Opened from the
 * explorer it starts with that entity, search, filters and columns already set.
 */
export function PullBlade({ open, onClose, depth, trail, onTrailClick, initial }: PullBladeProps) {
  const router = useRouter()
  const { t, n } = useTranslation()
  const { atsSystem, atsSource, error: atsError } = useTenantAtsSystem()
  const { loadEntityTypes, listRecords, pull } = useExplorer()
  const { exportOnboardingFile, progress: onboardingProgress, isExporting: isExportingOnboarding } = useOnboardingExport()

  const [step, setStep] = useState(0)
  const [entityTypes, setEntityTypes] = useState<AtsEntityTypeInfo[]>([])
  const [selectedEntityName, setSelectedEntityName] = useState(initial?.entityName ?? "")
  const [searchText, setSearchText] = useState(initial?.searchText ?? "")
  const [filterValues, setFilterValues] = useState<FilterValues>(initial?.filterValues ?? {})
  const [maxRecords, setMaxRecords] = useState(1000)
  const [relatedEntities, setRelatedEntities] = useState<AtsPullRelatedEntity[]>([])
  // Bare paths for single-entity pulls, "{entityKey}.{path}" keys for composite pulls
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set(initial?.selectedFields ?? []))
  // Layout per selected to-many association, keyed like the fields ("commissions", "Placement.commissions")
  const [toManyModes, setToManyModes] = useState<PullToManyModes>({})
  const [presets, setPresets] = useState<PullSelectionPreset[]>([])
  const [restoredSelection, setRestoredSelection] = useState(false)
  // Sections whose discovery failed; Next stays disabled until they are retried or removed
  const [failedSections, setFailedSections] = useState<Set<string>>(new Set())
  // Sections whose defaults were applied once their schema loaded
  // Columns carried in from the explorer count as the primary section's initial selection
  const [initializedSections, setInitializedSections] = useState<Set<string>>(
    new Set(initial?.entityName && initial.selectedFields?.length ? [initial.entityName] : [])
  )
  const [preview, setPreview] = useState<AtsRecordPage | null>(null)
  const [format, setFormat] = useState<AtsPullFormat>("Csv")
  const [valueFormat, setValueFormat] = useState<AtsPullValueFormat | null>(null)
  const [dropEmptyColumns, setDropEmptyColumns] = useState(true)
  const [includeRelatedEntities, setIncludeRelatedEntities] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPulling, setIsPulling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedEntityListing = useMemo(
    () => entityTypes.find((e) => e.name === selectedEntityName) ?? null,
    [entityTypes, selectedEntityName]
  )
  // Complete filters and default columns (the listing carries the curated subset)
  const { entity: selectedEntity, isLoading: isLoadingEntityDetail } = useEntityTypeDetail(atsSystem, selectedEntityListing)

  useEffect(() => {
    if (!atsSystem) return
    loadEntityTypes(atsSystem)
      .then(setEntityTypes)
      .catch((err) => setError(pullErrorMessage(err, t("pull.errors.loadEntityTypes"), t)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem])

  const missingRequiredFilters = selectedEntity ? getMissingRequiredFilters(selectedEntity.filters, filterValues) : []

  // Related entities cost one detail fetch per record, so multi-entity pulls cap lower
  const effectiveMaxRecordsLimit = relatedEntities.length > 0 ? PULL_MAX_RECORDS_COMPOSITE : PULL_MAX_RECORDS
  const isComposite = relatedEntities.length > 0

  const detailFetchableEntityTypes = useMemo(
    () => entityTypes.filter((e) => e.supportsDetailFetch),
    [entityTypes]
  )

  const relatedEntityIssue = useMemo(() => {
    if (relatedEntities.length === 0) return null
    if (relatedEntities.some((r) => !r.name.trim() || !r.entityType || !r.referencePath.trim())) {
      return "pull.relatedEntities.errorIncomplete"
    }
    // The name prefixes every field key ("Candidate.firstName"), so a dot would be read as part of the path
    if (relatedEntities.some((r) => r.name.includes("."))) {
      return "pull.relatedEntities.errorPeriod"
    }
    const names = relatedEntities.map((r) => r.name.trim().toLowerCase())
    if (new Set(names).size !== names.length || names.includes(selectedEntityName.toLowerCase())) {
      return "pull.relatedEntities.errorUnique"
    }
    return null
  }, [relatedEntities, selectedEntityName])

  const fieldSections = useMemo<FieldSection[]>(() => {
    if (!selectedEntity) return []
    const related = relatedEntities.map((r) => {
      const info = entityTypes.find((e) => e.name === r.entityType) ?? null
      return { key: r.name, title: `${r.name} (${info?.displayName ?? r.entityType})`, entityInfo: info }
    })
    return [{ key: selectedEntity.name, title: selectedEntity.displayName, entityInfo: selectedEntity }, ...related]
  }, [selectedEntity, relatedEntities, entityTypes])

  const keyForSection = (section: FieldSection) => (path: string) => (isComposite ? fieldKey(section.key, path) : path)
  const selectedRootKeys = useMemo(() => toManyRootKeysOf(selectedFields, isComposite), [selectedFields, isComposite])
  const rowsField = rowsRootKey(toManyModes, selectedRootKeys)
  const sheetFieldKeys = sheetRootKeys(toManyModes, selectedRootKeys)

  const resetFieldSelection = () => {
    setSelectedFields(new Set())
    setToManyModes({})
    setInitializedSections(new Set())
    setFailedSections(new Set())
    setRestoredSelection(false)
  }

  // A layout belongs to a selected association: deselecting its last path forgets it, and selecting past a Summary ends it
  const changeSelection = (next: Set<string>) => {
    setSelectedFields(next)
    setToManyModes((current) => activeToManyModes(current, next, isComposite))
    setRestoredSelection(false)
  }

  const currentSelection = (): PullSelection => ({
    fields: Array.from(selectedFields),
    toManyModes: activeToManyModes(toManyModes, selectedFields, isComposite),
    relatedEntities: isComposite ? relatedEntities : [],
  })

  // A remembered selection brings its related entities along, since its keys are prefixed by them
  const applySelection = (entityName: string, selection: PullSelection) => {
    setRelatedEntities(selection.relatedEntities)
    if (selection.relatedEntities.length > 0) setMaxRecords((records) => Math.min(records, PULL_MAX_RECORDS_COMPOSITE))
    setSelectedFields(new Set(selection.fields))
    setToManyModes(selection.toManyModes)
    setInitializedSections(new Set([entityName, ...selection.relatedEntities.map((related) => related.name)]))
    setFailedSections(new Set())
  }

  const storage = localSelectionStorage()
  const selectionStorageKey = atsSystem && atsSource && selectedEntityName ? pullSelectionStorageKey(atsSystem, atsSource, selectedEntityName) : null
  const presetsStorageKey = atsSystem && atsSource && selectedEntityName ? pullPresetsStorageKey(atsSystem, atsSource, selectedEntityName) : null

  useEffect(() => {
    setPresets(presetsStorageKey ? readPullPresets(storage, presetsStorageKey) : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetsStorageKey])

  // The last pull of this entity is restored once per entity; columns carried in from the explorer win
  const restoredFor = useRef<string | null>(null)
  useEffect(() => {
    if (!selectionStorageKey || restoredFor.current === selectionStorageKey) return
    restoredFor.current = selectionStorageKey
    if (initial?.carriedFromExplorer && initial.entityName === selectedEntityName && initial.selectedFields?.length) return
    const remembered = readStoredPullSelection(storage, selectionStorageKey)
    if (!remembered) return
    applySelection(selectedEntityName, remembered)
    setRestoredSelection(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionStorageKey])

  // The stored list is re-read before each write, so a preset saved from another tab survives
  const savePresets = (update: (current: PullSelectionPreset[]) => PullSelectionPreset[]) => {
    if (!presetsStorageKey) return
    const next = update(storage ? readPullPresets(storage, presetsStorageKey) : presets)
    setPresets(next)
    writePullPresets(storage, presetsStorageKey, next)
  }

  const forgetSection = (key: string) => {
    setSelectedFields((current) => withoutSectionKeys(current, key, true))
    setToManyModes((current) => withoutSectionModes(current, key))
    setInitializedSections((current) => {
      const next = new Set(current)
      next.delete(key)
      return next
    })
    setFailedSections((current) => {
      const next = new Set(current)
      next.delete(key)
      return next
    })
  }

  // Only a change of entity type invalidates a section's selection; a rename just re-keys it
  const updateRelatedEntity = (index: number, update: Partial<AtsPullRelatedEntity>) => {
    const previous = relatedEntities[index]
    setRelatedEntities((current) => current.map((r, i) => (i === index ? { ...r, ...update } : r)))
    if (!previous) return
    if (update.entityType !== undefined && update.entityType !== previous.entityType) {
      forgetSection(previous.name)
    } else if (update.name !== undefined && update.name !== previous.name) {
      setSelectedFields((current) => renameSectionKeys(current, previous.name, update.name!))
      setToManyModes((current) => renameSectionModes(current, previous.name, update.name!))
      setInitializedSections((current) => new Set(Array.from(current).map((key) => (key === previous.name ? update.name! : key))))
    }
  }

  /**
   * Preselects a section once its schema loads: the curated defaults for vendors with
   * field selection (Bullhorn — every field would otherwise flood the pull), every
   * flat column otherwise (fields only project the CSV there).
   */
  // A section whose discovery failed blocks Next until it is retried or removed: exporting without its
  // defaults would silently drop that entity's columns
  const handleSectionSettled = (section: FieldSection, fields: AtsFieldDescriptor[] | null) => {
    if (fields === null) {
      setFailedSections((current) => new Set(current).add(section.key))
      return
    }
    setFailedSections((current) => {
      const next = new Set(current)
      next.delete(section.key)
      return next
    })
    if (initializedSections.has(section.key)) return
    const keyFor = keyForSection(section)
    const defaults = section.entityInfo?.supportsFieldSelection
      ? (section.entityInfo.defaultFields ?? [])
      : fields.filter(isColumnField).map((f) => f.path)
    setSelectedFields((current) => new Set([...current, ...defaults.map(keyFor)]))
    setInitializedSections((current) => new Set(current).add(section.key))
  }
  const sectionsSettling = fieldSections.some((section) => !initializedSections.has(section.key) && !failedSections.has(section.key))
  const sectionsFailed = fieldSections.some((section) => failedSections.has(section.key))

  const goToPreview = async () => {
    if (!atsSystem || !selectedEntityName) return
    setIsLoading(true)
    setError(null)
    try {
      const page = await listRecords(atsSystem, {
        entityType: selectedEntityName,
        searchText: searchText || null,
        filters: toExplorerFilters(filterValues),
        // Field-selection vendors (Bullhorn) preview the chosen columns instead of the full field set
        fields: buildPreviewFields(selectedFields, selectedEntity, isComposite),
        pageSize: 10,
      })
      setPreview(page)
      setStep(2)
    } catch (err) {
      setError(pullErrorMessage(err, t("pull.errors.loadPreview"), t))
    } finally {
      setIsLoading(false)
    }
  }

  const handleFormatChange = (next: AtsPullFormat) => {
    setFormat(next)
    // Each format has its own sensible default; a deliberate choice is re-made per format
    setValueFormat(null)
  }

  const pulledRecords = (records: unknown[] | null): Record<string, unknown>[] => {
    if (records === null || !records.every(isRecord)) throw new Error(t("pull.errors.notRecords"))
    return records
  }

  // The sheets of an Excel pull with "Separate sheet" fields, each keyed by its parent's id column
  const sheetFields = (sheetKeys: string[]): SheetField[] =>
    sheetKeys.map((key) => ({ key, parentIdPath: parentIdPathOf(key, isComposite), columns: itemPathsUnder(selectedFields, key) }))

  const handlePull = async () => {
    if (!atsSystem || !selectedEntityName) return
    setIsPulling(true)
    setError(null)
    try {
      // Excel is a client-side conversion: of the CSV pull, or of a JSON pull when a field gets its own sheet
      const plan = planToMany(toManyModes, selectedRootKeys, format)
      const sheets = sheetFields(plan.sheetKeys)
      // A sheet is keyed by its parent's id, so the id is pulled even when it was not selected
      const exportFields = new Set([...selectedFields, ...sheets.map((sheet) => sheet.parentIdPath)])
      const { blob, fileName, records } = await pull(atsSystem, {
        entityType: selectedEntityName,
        searchText: searchText || null,
        filters: toExplorerFilters(filterValues),
        fields: buildPullFields({ selected: exportFields, primaryEntity: selectedEntity, format }),
        maxRecords: Math.min(maxRecords, effectiveMaxRecordsLimit),
        format: plan.format,
        valueFormat: valueFormat ?? defaultValueFormat(format),
        relatedEntities: isComposite ? relatedEntities : null,
        toMany: plan.toMany,
      })

      if (format === "Xlsx") {
        const sheetName = selectedEntity?.displayName ?? selectedEntityName
        const workbook =
          plan.sheetKeys.length > 0
            ? recordsToWorkbook(pulledRecords(records), Array.from(selectedFields), sheets, sheetName, dropEmptyColumns)
            : csvToWorkbook(await blob.text(), sheetName, dropEmptyColumns)
        const xlsxName = fileName.replace(/\.(csv|json)$/, ".xlsx")
        XLSX.writeFile(workbook, xlsxName)
        toast.success(t("pull.export.downloaded", { name: xlsxName }))
      } else {
        downloadBlob(blob, fileName)
        toast.success(t("pull.export.downloaded", { name: fileName }))
      }
      if (selectionStorageKey) writeStoredPullSelection(storage, selectionStorageKey, currentSelection())
    } catch (err) {
      setError(pullErrorMessage(err, t("pull.errors.pullFailed"), t))
    } finally {
      setIsPulling(false)
    }
  }

  const handleOnboardingExport = async () => {
    if (!atsSystem) return
    setError(null)
    try {
      await exportOnboardingFile(atsSystem, {
        maxRecords: Math.min(maxRecords, includeRelatedEntities ? PULL_MAX_RECORDS_COMPOSITE : PULL_MAX_RECORDS),
        includeRelatedEntities,
        searchText: searchText || null,
        // Carry the wizard's filters so the onboarding pull matches the previewed scope
        filters: toExplorerFilters(filterValues),
      })
      toast.success(t("pull.onboarding.downloaded"))
    } catch (err) {
      setError(pullErrorMessage(err, t("pull.errors.onboardingFailed"), t))
    }
  }

  const previewColumns = useMemo(() => {
    if (!preview || preview.records.length === 0) return []
    return Object.keys(preview.records[0])
      .filter((key) => typeof preview.records[0][key] !== "object" || preview.records[0][key] === null)
      .slice(0, 8)
  }, [preview])

  const effectiveValueFormat = valueFormat ?? defaultValueFormat(format)
  // Only an Excel file can hold a separate sheet: a CSV turns those fields into in-cell lists, a JSON file keeps every list nested
  const sheetFieldsWithoutExcel = format === "Csv" ? sheetFieldKeys : []
  const layoutsIgnoredByJson = format === "Json" && selectedRootKeys.some((key) => key in toManyModes)

  return (
    <Blade
      open={open}
      onClose={onClose}
      size="xl"
      depth={depth}
      trail={trail}
      onTrailClick={onTrailClick}
      eyebrow={selectedEntity ? selectedEntity.displayName : t("pull.header.eyebrow")}
      title={t("pull.header.title")}
      description={t("pull.header.step", {
        current: n(step + 1),
        total: n(STEP_KEYS.length),
        label: t(STEP_KEYS[step]),
      })}
      bare
    >
      <div className="flex min-h-full">
        <nav className="w-52 shrink-0 border-r border-card-border p-4" aria-label={t("pull.steps.navLabel")}>
          <ol className="space-y-1">
            {STEP_KEYS.map((stepKey, index) => (
              <li key={stepKey}>
                <button
                  type="button"
                  disabled={index > step}
                  onClick={() => index < step && setStep(index)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[13px] transition-colors ${
                    index === step ? "bg-accent font-semibold text-accent-foreground" : index < step ? "text-foreground hover:bg-muted" : "text-muted-foreground"
                  }`}
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${index <= step ? "bg-primary text-primary-foreground" : "border border-control-border"}`}>
                    {n(index + 1)}
                  </span>
                  {t(stepKey)}
                </button>
              </li>
            ))}
          </ol>
        </nav>
        <div className="min-w-0 flex-1 space-y-4 p-6">
      {atsError && (
        <Alert variant="destructive">
          <AlertDescription>{atsError}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step 1: Entity & search */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("pull.scope.title")}</CardTitle>
            <CardDescription>{t("pull.scope.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {initial?.carriedFromExplorer && initial.entityName && (
              <p className="rounded-md bg-accent/50 px-3 py-2 text-xs text-accent-foreground">
                {t("pull.scope.carriedOver")}
              </p>
            )}
            <div className="space-y-2 max-w-sm">
              <Label>{t("pull.scope.entityType")}</Label>
              <Select
                value={selectedEntityName}
                onValueChange={(value) => {
                  setSelectedEntityName(value)
                  setFilterValues({})
                  setRelatedEntities([])
                  resetFieldSelection()
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("pull.scope.entityPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <GroupedEntitySelectItems entities={entityTypes} />
                </SelectContent>
              </Select>
            </div>

            {selectedEntity?.supportsKeywordSearch && (
              <div className="space-y-2 max-w-sm">
                <Label>{t("pull.scope.keywordSearch")}</Label>
                <Input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder={t("pull.scope.keywordPlaceholder")} />
              </div>
            )}

            {atsSystem && selectedEntity && selectedEntity.supportsFilters && (
              <div className="space-y-2">
                <Label>{t("pull.scope.filters")}</Label>
                <FilterBar
                  entityName={selectedEntityName}
                  atsSystem={atsSystem}
                  definitions={selectedEntity.filters}
                  values={filterValues}
                  onChange={setFilterValues}
                  isLoadingDefinitions={isLoadingEntityDetail}
                />
              </div>
            )}

            {selectedEntityName && detailFetchableEntityTypes.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>{t("pull.relatedEntities.label")}</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRelatedEntities((current) => [...current, { name: "", entityType: "", referencePath: "" }])
                      // Multi-entity pulls cap lower; clamp eagerly so the input stays valid
                      setMaxRecords((records) => Math.min(records, PULL_MAX_RECORDS_COMPOSITE))
                      resetFieldSelection()
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" /> {t("pull.relatedEntities.add")}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("pull.relatedEntities.help", { entity: selectedEntity?.displayName.toLowerCase() ?? "" })}
                </p>
                {relatedEntities.map((related, index) => (
                  <div key={index} className="space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-end">
                      <div className="space-y-1">
                        <Label className="text-xs">{t("pull.relatedEntities.entityType")}</Label>
                        <Select
                          value={related.entityType}
                          onValueChange={(value) =>
                            updateRelatedEntity(index, {
                              entityType: value,
                              // Default the output key to the entity type until the user names it
                              name: related.name.trim() === "" || related.name === related.entityType ? value : related.name,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t("pull.relatedEntities.entityPlaceholder")} />
                          </SelectTrigger>
                          <SelectContent>
                            <GroupedEntitySelectItems entities={detailFetchableEntityTypes.filter((e) => e.name !== selectedEntityName)} />
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t("pull.relatedEntities.name")}</Label>
                        <Input
                          value={related.name}
                          onChange={(e) => updateRelatedEntity(index, { name: e.target.value })}
                          placeholder={t("pull.relatedEntities.namePlaceholder")}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t("pull.relatedEntities.referencePath")}</Label>
                        {atsSystem ? (
                          <AtsFieldPathPicker
                            entityType={selectedEntityName}
                            value={related.referencePath || null}
                            onChange={(value) => updateRelatedEntity(index, { referencePath: value ?? "" })}
                            placeholder={t("pull.relatedEntities.referencePathPlaceholder")}
                          />
                        ) : (
                          <Input
                            value={related.referencePath}
                            onChange={(e) => updateRelatedEntity(index, { referencePath: e.target.value })}
                            placeholder={t("pull.relatedEntities.referencePathPlaceholder")}
                          />
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setRelatedEntities((current) => current.filter((_, i) => i !== index))
                          resetFieldSelection()
                        }}
                        title={t("pull.relatedEntities.remove")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {related.referencePath.includes("[") && (
                      <ArrayPathRefiner
                        entityType={selectedEntityName}
                        path={related.referencePath}
                        onChange={(value) => updateRelatedEntity(index, { referencePath: value })}
                        selection="single"
                      />
                    )}
                  </div>
                ))}
                {relatedEntityIssue && <p className="text-xs text-amber-600">{t(relatedEntityIssue)}</p>}
              </div>
            )}

            <div className="space-y-2 max-w-sm">
              <Label>{t("pull.scope.maxRecords", { limit: n(effectiveMaxRecordsLimit) })}</Label>
              <Input
                type="number"
                min={1}
                max={effectiveMaxRecordsLimit}
                value={maxRecords}
                onChange={(e) => setMaxRecords(Math.max(1, Math.min(effectiveMaxRecordsLimit, Number(e.target.value) || 1)))}
              />
              {isComposite && (
                <p className="text-xs text-muted-foreground">
                  {t("pull.scope.compositeCap", { limit: n(PULL_MAX_RECORDS_COMPOSITE) })}
                </p>
              )}
            </div>

            <div className="flex justify-end items-center gap-3">
              {missingRequiredFilters.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t("pull.scope.requiredFilters", { names: missingRequiredFilters.map((f) => f.displayName).join(", ") })}
                </span>
              )}
              <Button
                onClick={() => setStep(1)}
                disabled={!selectedEntityName || missingRequiredFilters.length > 0 || relatedEntityIssue !== null}
              >
                {t("common.actions.next")} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Field selection */}
      {step === 1 && selectedEntity && (
        <Card>
          <CardHeader>
            <CardTitle>{t("pull.fields.title")}</CardTitle>
            <CardDescription>
              {t((selectedEntity.supportsFieldSelection ? "pull.fields.descriptionColumnsVendor" : "pull.fields.descriptionColumns"))}
              {isComposite && (
                <> {t("pull.fields.descriptionComposite", {
                  primary: selectedEntity.name,
                  related: relatedEntities[0]?.name || t("pull.fields.relatedFallback"),
                })}</>
              )}
              {" "}{t("pull.fields.descriptionExpand")}
              {" "}{t("pull.fields.descriptionToMany")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {presetsStorageKey && (
              <div className="flex items-center gap-3">
                <SavedSelectionsMenu
                  presets={presets}
                  canSave={selectedFields.size > 0}
                  onSave={(name) => savePresets((current) => withPreset(current, name, currentSelection()))}
                  onApply={(preset) => {
                    applySelection(selectedEntityName, preset)
                    setRestoredSelection(false)
                  }}
                  onDelete={(name) => savePresets((current) => withoutPreset(current, name))}
                />
                {restoredSelection && <p className="text-xs text-muted-foreground">{t("pull.savedSelections.restored")}</p>}
              </div>
            )}
            {fieldSections.map((section) => (
              <FieldSelectionSection
                key={section.key}
                title={section.title}
                entityInfo={section.entityInfo}
                filters={section.key === selectedEntity.name ? toExplorerFilters(filterValues) : undefined}
                sectionKey={section.key}
                isComposite={isComposite}
                keyFor={keyForSection(section)}
                selected={selectedFields}
                onChange={changeSelection}
                toManyModes={toManyModes}
                onToManyModeChange={(rootKey, mode) => setToManyModes((current) => ({ ...current, [rootKey]: mode }))}
                rowsRootKey={rowsField}
                sheetRootKeys={sheetFieldKeys}
                onSettled={(fields) => handleSectionSettled(section, fields)}
              />
            ))}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(0)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> {t("common.actions.back")}
              </Button>
              <Button onClick={goToPreview} disabled={isLoading || sectionsSettling || sectionsFailed || (selectedEntity.supportsFieldSelection && selectedFields.size === 0)}
                title={sectionsFailed ? t("pull.fields.retryOrRemove") : undefined}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("common.actions.next")} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("pull.preview.title")}</CardTitle>
            <CardDescription>
              {preview?.totalCount != null
                ? t("pull.preview.countOfTotal", { count: n(preview.records.length), total: n(preview.totalCount) })
                : t("pull.preview.count", { count: n(preview?.records.length ?? 0) })}
              {isComposite ? ` ${t("pull.preview.compositeNote")}` : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {preview && preview.records.length > 0 ? (
              <div className="border rounded-md max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {previewColumns.map((column) => (
                        <TableHead key={column} className="whitespace-nowrap">{column}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.records.map((record, index) => (
                      <TableRow key={index}>
                        {previewColumns.map((column) => (
                          <TableCell key={column} className="text-xs max-w-48 truncate">
                            {record[column] === null || record[column] === undefined ? "" : String(record[column])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("pull.preview.empty")}</p>
            )}
            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> {t("common.actions.back")}
              </Button>
              <Button onClick={() => setStep(3)}>
                {t("common.actions.next")} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Export */}
      {step === 3 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("pull.export.title")}</CardTitle>
              <CardDescription>
                {t((isComposite ? "pull.export.descriptionComposite" : "pull.export.description"), {
                  count: n(Math.min(maxRecords, effectiveMaxRecordsLimit)),
                  entity: selectedEntity?.displayName.toLowerCase() ?? "",
                  related: relatedEntities.map((r) => r.name).join(", "),
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t("pull.export.fileLabel")}</Label>
                <RadioGroup value={format} onValueChange={(value) => handleFormatChange(value as AtsPullFormat)} className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="Xlsx" /> {t("pull.export.fileExcel")}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="Csv" /> {t("pull.export.fileCsv")}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="Json" /> {t("pull.export.fileJson")}
                  </label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t("pull.export.valuesLabel")}</Label>
                <RadioGroup
                  value={effectiveValueFormat}
                  onValueChange={(value) => setValueFormat(value as AtsPullValueFormat)}
                  className="flex gap-6"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="Friendly" /> {t("pull.export.valueFriendly")}
                    <span className="text-xs text-muted-foreground">{t("pull.export.valueFriendlyHint")}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="Raw" /> {t("pull.export.valueRaw")}
                    <span className="text-xs text-muted-foreground">{t("pull.export.valueRawHint")}</span>
                  </label>
                </RadioGroup>
              </div>

              {format === "Xlsx" && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={dropEmptyColumns} onCheckedChange={(checked) => setDropEmptyColumns(checked === true)} />
                  {t("pull.export.dropEmptyColumns")}
                </label>
              )}

              {sheetFieldsWithoutExcel.length > 0 && (
                <p className="text-xs text-amber-600">{t("pull.toMany.sheetNeedsExcel", { fields: sheetFieldsWithoutExcel.join(", ") })}</p>
              )}
              {layoutsIgnoredByJson && <p className="text-xs text-muted-foreground">{t("pull.toMany.jsonNested")}</p>}

              <Button onClick={handlePull} disabled={isPulling}>
                {isPulling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                {t("pull.export.download", { format: format === "Xlsx" ? "Excel" : format.toUpperCase() })}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("pull.onboarding.title")}</CardTitle>
              <CardDescription>{t("pull.onboarding.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={includeRelatedEntities} onCheckedChange={setIncludeRelatedEntities} />
                {t("pull.onboarding.includeRelated", { limit: n(PULL_MAX_RECORDS_COMPOSITE) })}
              </label>

              {onboardingProgress.length > 0 && (
                <div className="space-y-1 text-sm">
                  {onboardingProgress.map((p) => (
                    <div key={p.sheetName} className="flex items-center gap-2">
                      {p.status === "pulling" && <Loader2 className="h-4 w-4 animate-spin" />}
                      {p.status === "done" && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      {(p.status === "error" || p.status === "skipped") && <XCircle className="h-4 w-4 text-amber-600" />}
                      {p.status === "pending" && <span className="h-4 w-4" />}
                      <span>{p.sheetName}</span>
                      {p.rowCount !== undefined && (
                        <span className="text-muted-foreground">
                          {t((p.rowCount === 1 ? "pull.onboarding.rowsOne" : "pull.onboarding.rowsOther"), { count: n(p.rowCount) })}
                        </span>
                      )}
                      {p.message && <span className="text-muted-foreground text-xs">— {p.message}</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleOnboardingExport} disabled={isExportingOnboarding}>
                  {isExportingOnboarding ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  {t("pull.onboarding.generate")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    // The hub's blade stack survives ?view= changes, so the blade must close itself first
                    onClose()
                    router.push("?view=target-mapping")
                  }}
                >
                  {t("pull.onboarding.configureTargetMapping")}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-start">
            <Button variant="outline" onClick={() => setStep(2)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> {t("common.actions.back")}
            </Button>
          </div>
        </div>
      )}
        </div>
      </div>
    </Blade>
  )
}
